import { createContext, useContext, useEffect, useMemo, useState } from "react";

const CartContext = createContext(null);
const STORAGE_KEY = "rinova_cart";

function lineId(item) {
  return [item.product_id, item.grade, item.color_id || "", item.memory || ""].join("::");
}

export function CartProvider({ children }) {
  const [items, setItems] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : [];
    } catch {
      return [];
    }
  });
  const [drawerOpen, setDrawerOpen] = useState(false);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    } catch {}
  }, [items]);

  const openDrawer = () => setDrawerOpen(true);
  const closeDrawer = () => setDrawerOpen(false);

  const addItem = (item, qty = 1, { openCart = true } = {}) => {
    setItems((prev) => {
      const id = lineId(item);
      const existing = prev.find((i) => i._lineId === id);
      if (existing) {
        return prev.map((i) =>
          i._lineId === id ? { ...i, quantity: i.quantity + qty } : i
        );
      }
      return [...prev, { ...item, quantity: qty, _lineId: id }];
    });
    if (openCart) setDrawerOpen(true);
  };

  const updateQty = (lineId_, qty) => {
    setItems((prev) =>
      prev
        .map((i) => (i._lineId === lineId_ ? { ...i, quantity: Math.max(1, qty) } : i))
        .filter((i) => i.quantity > 0)
    );
  };

  const removeItem = (lineId_) => {
    setItems((prev) => prev.filter((i) => i._lineId !== lineId_));
  };

  const clearCart = () => setItems([]);

  const count = useMemo(
    () => items.reduce((sum, i) => sum + i.quantity, 0),
    [items]
  );
  const subtotal = useMemo(
    () => items.reduce((sum, i) => sum + i.unit_price * i.quantity, 0),
    [items]
  );

  const value = { items, addItem, updateQty, removeItem, clearCart, count, subtotal, drawerOpen, setDrawerOpen, openDrawer, closeDrawer };
  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart() {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used within CartProvider");
  return ctx;
}
